// /settings/billing — admin-only billing surface. Current-plan card,
// the upsell tier-strip.
// Non-admin members get a "not available" card; server-side gates on
// /api/billing/* are the authority, this is UX.

import { PageChrome } from "@/components/chrome/PageChrome";
import { InvoicesPanel } from "@/components/settings/billing/InvoicesPanel";
import { PaymentMethodPanel } from "@/components/settings/billing/PaymentMethodPanel";
import { PlanSummary } from "@/components/settings/billing/PlanSummary";
import { TierStrip } from "@/components/settings/billing/TierStrip";
import { Card, LoadingState, RouteShell } from "@/design-system";
import { useEntitlements } from "@/hooks/useBilling";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useOrganization } from "@/lib/auth";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";

export const Route = createFileRoute("/settings/billing")({
  component: BillingPage,
});

function BillingPage() {
  const { organization } = useOrganization();
  const capabilities = useCapabilities();
  const entitlements = useEntitlements();

  if (!capabilities.loaded) {
    return <LoadingState />;
  }

  if (!capabilities.canManageBilling) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 p-8">
        <Card className="max-w-md w-full p-8 flex flex-col items-center gap-4 text-center">
          <Lock size={28} className="text-fg-3" strokeWidth={1.5} />
          <h1 className="text-[20px] font-medium text-fg-1">
            Not available for your role
          </h1>
          <p className="text-[14px] text-fg-2 leading-relaxed">
            Billing is only available to org admins. Ask an admin to manage your
            plan, payment method, or invoices.
          </p>
          <Link
            to="/settings/organization"
            className="text-small text-accent hover:underline"
          >
            ← Back to settings
          </Link>
        </Card>
      </div>
    );
  }

  const orgName = organization?.name ?? "";

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <PageChrome
        crumbs={[
          { label: orgName || "Org", to: "/dashboard" },
          { label: "Settings" },
          { label: "Billing" },
        ]}
        title="Billing"
        helper="Your plan, payment method, and invoices."
      />
      <div className="flex-1 overflow-auto px-4 sm:px-7 py-6">
        <div className="flex flex-col gap-6">
          <RouteShell
            isPending={entitlements.isPending}
            error={entitlements.error}
          >
            {entitlements.data ? (
              <>
                <PlanSummary />
                <TierStrip />
                <PaymentMethodPanel />
                <InvoicesPanel />
              </>
            ) : null}
          </RouteShell>
        </div>
      </div>
    </div>
  );
}
